/**
 * Toast feedback pro OVĚŘENÉ write operace (BMS / PIP / Tracer).
 *
 * Vstupem je per-pole výsledek z {@link WriteFieldResult} — hodnoty `requested`
 * a `actual` pochází z reálného read-backu ze zařízení (server porovnal po
 * zápisu). UI tedy nic nedosazuje; jen pravdivě hlásí, co zařízení potvrdilo.
 */

import { toast } from "sonner";
import { formatNumber } from "@globals/format";
import type { WriteFieldResult } from "@client/types/verify";

const numOrStr = (v: number | string | undefined): string =>
  typeof v === "number" ? formatNumber(v) : v === undefined ? "—" : String(v);

const bothNumeric = (r: WriteFieldResult): boolean => typeof r.requested === "number" && typeof r.actual === "number";

/**
 * Zobrazí toasty pro výsledek dávkového ověřeného zápisu (per-pole).
 * `labels` mapuje field klíč na lidský popisek (z dictionary).
 */
export const showWriteResults = (results: WriteFieldResult[], labels: Record<string, string> = {}): void => {
  const label = (f: string) => labels[f] ?? f;
  const confirmed = results.filter((r) => r.status === "confirmed");
  const mismatch = results.filter((r) => r.status === "mismatch");
  const unconfirmed = results.filter((r) => r.status === "unconfirmed");
  const rejected = results.filter((r) => r.status === "rejected");

  // Vše potvrzeno → jediný success toast.
  if (mismatch.length === 0 && unconfirmed.length === 0 && rejected.length === 0) {
    toast.success(`✓ Uloženo a ověřeno (${confirmed.length})`);
    return;
  }
  if (confirmed.length > 0) toast.success(`✓ Ověřeno: ${confirmed.length}`);
  for (const r of mismatch) {
    toast.warning(
      bothNumeric(r)
        ? `⚠ ${label(r.field)}: zapsáno ${numOrStr(r.requested)}, ale zařízení hlásí ${numOrStr(r.actual)}`
        : `⚠ ${label(r.field)}: zařízení hlásí jinou hodnotu (viz aktuální stav)`
    );
  }
  for (const r of unconfirmed) {
    toast.warning(`⚠ ${label(r.field)}: zapsáno, nelze ověřit${r.reason ? ` (${r.reason})` : ""}`);
  }
  for (const r of rejected) {
    toast.error(`✗ ${label(r.field)}: odmítnuto${r.reason ? ` — ${r.reason}` : ""}`);
  }
};

/** Toast pro jeden ověřený zápis (control) s vlastním popiskem. */
export const showWriteResult = (result: WriteFieldResult, label: string): void => {
  switch (result.status) {
    case "confirmed":
      toast.success(`✓ ${label} ověřeno`);
      return;
    case "mismatch":
      toast.warning(
        bothNumeric(result)
          ? `⚠ ${label}: zapsáno ${numOrStr(result.requested)}, ale zařízení hlásí ${numOrStr(result.actual)}`
          : `⚠ ${label}: zařízení hlásí jinou hodnotu (viz aktuální stav)`
      );
      return;
    case "unconfirmed":
      toast.warning(`⚠ ${label}: zapsáno, nelze ověřit${result.reason ? ` (${result.reason})` : ""}`);
      return;
    case "rejected":
      toast.error(`✗ ${label}: odmítnuto${result.reason ? ` — ${result.reason}` : ""}`);
      return;
  }
};
